import { pb } from './pocketbase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Récupérer les groupes de sessions avec pagination et filtres
export const getSessionGroups = async (options = {}) => {
  try {
    const { page = 1, perPage = 10, filter = null, sort = '-created' } = options;
    
    const result = await pb.collection('session_groups').getList(page, perPage, {
      filter,
      sort,
      expand: 'partner_id'
    });
    
    // Récupérer le nombre de membres pour chaque groupe
    const itemsWithMemberCount = await Promise.all(
      result.items.map(async (group) => {
        try {
          const members = await pb.collection('session_group_members').getList(1, 1, {
            filter: `session_group_id="${group.id}"`,
            skipTotal: false
          });
          return { ...group, memberCount: members.totalItems };
        } catch (error) {
          console.error(`Erreur lors du comptage des membres pour le groupe ${group.id}:`, error);
          return { ...group, memberCount: 0 };
        }
      })
    );
    
    return {
      items: itemsWithMemberCount,
      totalPages: result.totalPages,
      totalItems: result.totalItems,
      page: result.page,
      perPage: result.perPage
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des groupes de sessions:', error);
    throw error;
  }
};

// Récupérer un groupe de session par ID
export const getSessionGroupById = async (id) => {
  try {
    return await pb.collection('session_groups').getOne(id);
  } catch (error) {
    console.error('Erreur lors de la récupération du groupe de session:', error);
    throw error;
  }
};

// Récupérer un groupe de session avec ses membres
export const getSessionGroupWithMembers = async (id) => {
  try {
    // Récupérer le groupe
    const group = await pb.collection('session_groups').getOne(id);
    
    // Récupérer les membres
    const members = await pb.collection('session_group_members').getFullList({
      filter: `session_group_id="${id}"`
    });
    
    // Ajouter les membres au groupe
    group.members = members;
    group.students_id = members.map(member => member.student_id);
    
    return group;
  } catch (error) {
    console.error('Erreur lors de la récupération du groupe avec membres:', error);
    throw error;
  }
};

// Récupérer les membres d'un groupe de session
export const getSessionGroupMembers = async (sessionGroupId) => {
  try {
    const members = await pb.collection('session_group_members').getFullList({
      filter: `session_group_id="${sessionGroupId}"`,
      expand: 'student_id,user_id'
    });
    return members;
  } catch (error) {
    console.error('Erreur lors de la récupération des membres du groupe:', error);
    throw error;
  }
};

// Créer un nouveau groupe de session avec ses membres
export const createSessionGroup = async (data) => {
  try {
    // 1) Création du groupe
    const group = await pb.collection('session_groups').create({
      title: data.title,
      partner_id: data.partner_id
    });

    // 2) Pour chaque étudiant sélectionné, créer un membre du groupe
    if (data.students_id && data.students_id.length > 0) {
      for (const studentId of data.students_id) {
        await pb.collection('session_group_members').create({
          session_group_id: group.id,
          student_id: studentId
        });
      }
    }

    return group;
  } catch (error) {
    console.error('Erreur lors de la création du groupe de session:', error);
    throw error;
  }
};

// Mettre à jour un groupe de session et ses membres
export const updateSessionGroup = async (id, data) => {
  try {
    // 1) Mettre à jour les informations du groupe
    const updateData = {
      title: data.title
    };
    
    // Ajouter partner_id seulement s'il est fourni
    if (data.partner_id) {
      updateData.partner_id = data.partner_id;
    }
    
    const updatedGroup = await pb.collection('session_groups').update(id, updateData);

    // 2) Gérer les membres si students_id est fourni
    if (data.students_id && Array.isArray(data.students_id)) {
      // Récupérer les membres actuels
      const currentMembers = await pb.collection('session_group_members').getFullList({
        filter: `session_group_id="${id}"`
      });

      const currentStudentIds = currentMembers.map(member => member.student_id);
      const newStudentIds = data.students_id;

      // Supprimer les membres qui ne sont plus dans la nouvelle liste
      const membersToRemove = currentMembers.filter(
        member => !newStudentIds.includes(member.student_id)
      );
      for (const member of membersToRemove) {
        await pb.collection('session_group_members').delete(member.id);
      }

      // Ajouter les nouveaux membres
      const studentsToAdd = newStudentIds.filter(
        studentId => !currentStudentIds.includes(studentId)
      );
      for (const studentId of studentsToAdd) {
        await pb.collection('session_group_members').create({
          session_group_id: id,
          student_id: studentId
        });
      }
    }

    return updatedGroup;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du groupe de session:', error);
    throw error;
  }
};

// Supprimer un groupe de session et ses membres (suppression en cascade)
export const deleteSessionGroup = async (id) => {
  try {
    // 1) Récupérer et supprimer tous les membres du groupe
    const members = await pb.collection('session_group_members').getFullList({
      filter: `session_group_id="${id}"`
    });
    
    // Suppression des membres
    if (members.length > 0) {
      for (const member of members) {
        await pb.collection('session_group_members').delete(member.id);
      }
    }
    
    // 2) Supprimer le groupe principal
    return await pb.collection('session_groups').delete(id);
  } catch (error) {
    console.error('Erreur lors de la suppression en cascade:', error);
    throw error;
  }
};

// Dupliquer un groupe de session avec ses membres
export const duplicateSessionGroup = async (id) => {
  try {
    // 1) Récupérer le groupe original
    const original = await getSessionGroupById(id);
    
    // 2) Récupérer les membres du groupe original
    const originalMembers = await getSessionGroupMembers(id);
    
    // 3) Créer le nouveau groupe avec les mêmes membres
    const duplicateData = {
      title: `${original.title} (Copie)`,
      partner_id: original.partner_id,
      students_id: originalMembers.map(member => member.student_id)
    };
    
    return await createSessionGroup(duplicateData);
  } catch (error) {
    console.error('Erreur lors de la duplication du groupe de session:', error);
    throw error;
  }
};

// Supprimer plusieurs groupes de sessions (suppression en cascade multiple)
export const deleteMultipleSessionGroups = async (ids) => {
  try {
    // Supprimer les groupes un par un pour gérer les membres
    for (const id of ids) {
      await deleteSessionGroup(id);
    }
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression multiple en cascade:', error);
    throw error;
  }
};

// Dupliquer plusieurs groupes de sessions
export const duplicateMultipleSessionGroups = async (ids) => {
  try {
    const duplicatedGroups = [];
    for (const id of ids) {
      const duplicated = await duplicateSessionGroup(id);
      duplicatedGroups.push(duplicated);
    }
    return duplicatedGroups;
  } catch (error) {
    console.error('Erreur lors de la duplication multiple des groupes de sessions:', error);
    throw error;
  }
};
